import { lazy, Suspense, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import MobileNav from './components/MobileNav';
import PageSpinner from './components/PageSpinner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { AchievementProvider, useAchievement } from './contexts/AchievementContext';
import AchievementPopup from './components/AchievementPopup';
import InstagramBanner from './components/InstagramBanner';
import { API_URL } from './config';
import './App.css';
import { toast } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ReactQueryDevtoolsLazy from './components/ReactQueryDevtoolsLazy';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from './hooks/useAnalytics';
import ErrorBoundary from './components/ErrorBoundary';
import { authenticatedFetch } from './utils/apiUtils';
import { clearCache } from './utils/hikeCache';

const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const MyList = lazy(() => import('./pages/MyList'));
const MyAccount = lazy(() => import('./pages/MyAccount'));
const TopHikes = lazy(() => import('./pages/TopHikes'));
const Resources = lazy(() => import('./pages/Resources'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Users = lazy(() => import('./pages/Users'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const HikeProfile = lazy(() => import('./pages/HikeProfile'));
const Friends = lazy(() => import('./pages/Friends'));
const Timeline = lazy(() => import('./pages/Timeline'));

const AddHikeForm = lazy(() => import('./components/AddHikeForm'));
const EditHikeForm = lazy(() => import('./components/EditHikeForm'));
const AchievementTest = lazy(() => import('./components/AchievementTest'));
const AuthDebugPanel = lazy(() =>
  import('./components/AuthDebugPanel').then((mod) => ({ default: mod.AuthDebugPanel }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function AppContent() {
  const [isAddHikeOpen, setIsAddHikeOpen] = useState(false);
  const [isEditHikeOpen, setIsEditHikeOpen] = useState(false);
  const [editingHike, setEditingHike] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { user } = useAuth();
  const { currentAchievements, isPopupVisible, hideAchievementPopup } = useAchievement();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useAnalytics();

  const handleEditHike = (hike: any) => {
    setEditingHike(hike);
    setIsEditHikeOpen(true);
  };

  const handleAddHike = async (hikeData: any) => {
    try {
      if (!user) {
        throw new Error('Not authenticated');
      }

      const userResponse = await authenticatedFetch(`${API_URL}/users/${user._id}`);

      if (userResponse.ok) {
        const updatedUserData = await userResponse.json();
        localStorage.setItem('user', JSON.stringify(updatedUserData));
      }

      setIsAddHikeOpen(false);
      setRefreshKey((prev) => prev + 1);
      toast.success('Hike added successfully!');

      window.dispatchEvent(
        new CustomEvent('hikesUpdated', {
          detail: {
            action: 'add',
            hike: hikeData,
            userStats: hikeData.userStats,
          },
        })
      );
      window.dispatchEvent(new CustomEvent('userDataUpdated'));
      clearCache();
    } catch (error) {
      console.error('Error handling hike addition:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to handle hike addition';
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleEditSubmit = () => {
    setIsEditHikeOpen(false);
    setEditingHike(null);
    setRefreshKey((prev) => prev + 1);
    window.dispatchEvent(new CustomEvent('hikesUpdated', { detail: { action: 'edit' } }));
    window.dispatchEvent(new CustomEvent('userDataUpdated'));
    clearCache();
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <Header />
      {isHomePage && <InstagramBanner />}
      <main className="flex-grow pb-16 md:pb-0">
        <Suspense fallback={<PageSpinner />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/my-list"
              element={
                <ProtectedRoute>
                  <MyList
                    onAddHikeClick={() => setIsAddHikeOpen(true)}
                    onEditHikeClick={handleEditHike}
                    refreshKey={refreshKey}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-account"
              element={
                <ProtectedRoute>
                  <MyAccount />
                </ProtectedRoute>
              }
            />
            <Route
              path="/friends"
              element={
                <ProtectedRoute>
                  <Friends />
                </ProtectedRoute>
              }
            />
            <Route
              path="/timeline"
              element={
                <ProtectedRoute>
                  <Timeline />
                </ProtectedRoute>
              }
            />
            <Route path="/top" element={<TopHikes />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/users" element={<Users />} />
            <Route path="/user/:username" element={<UserProfile />} />
            <Route path="/user/:username/list" element={<UserProfile viewOnly={true} />} />
            <Route path="/hike/:hikeName/:country" element={<HikeProfile />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <MobileNav />

      {isAddHikeOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="w-full max-w-md mx-4">
            <Suspense fallback={<PageSpinner label="Loading form..." />}>
              <AddHikeForm
                isOpen={isAddHikeOpen}
                onClose={() => setIsAddHikeOpen(false)}
                onSubmit={handleAddHike}
              />
            </Suspense>
          </div>
        </div>
      )}

      {isEditHikeOpen && editingHike && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="w-full max-w-md mx-4">
            <Suspense fallback={<PageSpinner label="Loading form..." />}>
              <EditHikeForm
                isOpen={isEditHikeOpen}
                onClose={() => {
                  setIsEditHikeOpen(false);
                  setEditingHike(null);
                }}
                onSubmit={handleEditSubmit}
                hike={editingHike}
              />
            </Suspense>
          </div>
        </div>
      )}

      <AchievementPopup
        achievements={currentAchievements}
        isVisible={isPopupVisible}
        onClose={hideAchievementPopup}
      />

      {import.meta.env.DEV && (
        <Suspense fallback={null}>
          <AchievementTest />
          <AuthDebugPanel />
        </Suspense>
      )}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <DarkModeProvider>
              <AchievementProvider>
                <Router>
                  <AppContent />
                </Router>
              </AchievementProvider>
            </DarkModeProvider>
            <ReactQueryDevtoolsLazy />
          </AuthProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, getDebugLogs } = useAuth();

  if (isLoading) {
    return <PageSpinner fullScreen />;
  }

  const logs = getDebugLogs();
  const lastLog = logs[logs.length - 1];
  const redirectsPaused = lastLog?.message?.includes('Redirects paused');

  if (!isAuthenticated && !redirectsPaused) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
}

export default App;
